/**
 * Talking to the server.
 *
 * Every call carries the signed-in user's token and this device's id. When the
 * connection is down the call fails fast and the caller falls back to whatever
 * is cached on the device - it never hangs the till.
 */
import { deviceId } from './device.js';
import { keyIn, shopKey, thisShop } from './shop.js';

const TOKEN_KEY = 'pos.token';
/** Fired on `window` when the server says the session is no longer valid. */
export const SESSION_ENDED_EVENT = 'pos:session-ended';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
  }
  /** True when there is simply no connection, as opposed to a refusal. */
  get isOffline(): boolean {
    return this.status === 0;
  }
}

export function getToken(): string | null {
  return localStorage.getItem(shopKey(TOKEN_KEY));
}
/** Store a session. `storageKey` names another shop, when signing in to switch to it. */
export function setToken(t: string | null, storageKey?: string): void {
  const key = storageKey === undefined ? shopKey(TOKEN_KEY) : keyIn(storageKey, TOKEN_KEY);
  if (t) localStorage.setItem(key, t);
  else localStorage.removeItem(key);
}

/** Which shop a call is for: the one this till belongs to, unless told otherwise. */
function accountHeaders(account: string | null | undefined): Record<string, string> {
  const code = account === undefined ? thisShop()?.code : account;
  return code ? { 'X-Pos-Account': code } : {};
}

type Options = {
  method?: string;
  body?: unknown;
  timeoutMs?: number;
  auth?: boolean;
  /**
   * A token from /auth/approve. It names the permissions a manager authorised
   * and lasts two minutes, so it cannot be kept and reused for something else.
   */
  approvalToken?: string;
  /**
   * The account code to send instead of this till's own - the sign-in screen
   * asking about the code just typed. `null` sends none: the default shop.
   */
  account?: string | null;
};

export async function api<T = unknown>(path: string, opts: Options = {}): Promise<T> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), opts.timeoutMs ?? 15_000);

  const headers: Record<string, string> = { 'X-Device-Id': deviceId(), ...accountHeaders(opts.account) };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  const token = getToken();
  if (token && opts.auth !== false) headers.Authorization = `Bearer ${token}`;
  if (opts.approvalToken) headers['X-Approval'] = opts.approvalToken;

  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      method: opts.method ?? (opts.body === undefined ? 'GET' : 'POST'),
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: controller.signal,
    });
  } catch {
    throw new ApiError('Cannot reach the system.', 0, 'offline');
  } finally {
    clearTimeout(timeout);
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = { message: text };
  }

  if (!res.ok) {
    const d = data as { message?: string; error?: string } | null;
    // A 401 on a call that CARRIED a token means the session is over (expired,
    // or signed out elsewhere). A wrong password on sign-in or on a manager
    // approval is also a 401, but says nothing about the current session.
    if (res.status === 401 && token && opts.auth !== false && d?.error !== 'bad_login') {
      setToken(null);
      window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT));
    }
    throw new ApiError(d?.message ?? 'That did not work.', res.status, d?.error ?? 'request_failed');
  }
  return data as T;
}

/** Download a file the server generates, such as an Excel export. */
export async function download(path: string, fallbackName: string): Promise<void> {
  const res = await fetch(`/api${path}`, {
    headers: { Authorization: `Bearer ${getToken() ?? ''}`, 'X-Device-Id': deviceId(), ...accountHeaders(undefined) },
  });
  if (!res.ok) throw new ApiError('The export could not be made.', res.status, 'export_failed');

  const disposition = res.headers.get('Content-Disposition') ?? '';
  const named = /filename="?([^"]+)"?/.exec(disposition)?.[1];
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = named ?? fallbackName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
